import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useCurrentUser, useUserMap } from '@/features/users/users.hooks';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { UserAvatar } from '@/components/ui/UserAvatar';
import { cn } from '@/lib/utils';
import { decode as decodePermissions } from '@certhon/cloud-permissions';

interface UserSwitcherProps {
  selectedUserId: number | null;
  onUserChange: (userId: number | null) => void;
}

export function UserSwitcher({
  selectedUserId,
  onUserChange,
}: UserSwitcherProps) {
  const currentUser = useCurrentUser();
  const userMap = useUserMap<'recnum'>('recnum');
  const [open, setOpen] = useState(false);

  const internalUsers = useMemo(() => {
    return Array.from(userMap.values())
      .filter(
        (user) =>
          user.internal === true && user.active === true && !!user.loginname,
      )
      .sort((a, b) => {
        const aName = a.name || a.loginname || '';
        const bName = b.name || b.loginname || '';
        return aName.localeCompare(bName);
      });
  }, [userMap]);

  const canSwitchUsers = useMemo(() => {
    if (!currentUser?.internal) return false;
    const userPermissions = decodePermissions(currentUser.permissions ?? 0);
    return userPermissions.WB_ADMIN || userPermissions.QA_ADMIN;
  }, [currentUser]);

  if (!canSwitchUsers || internalUsers.length === 0) {
    return null;
  }

  const effectiveUserId = selectedUserId ?? currentUser?.recnum;
  const selectedUser = effectiveUserId ? userMap.get(effectiveUserId) : null;
  const selectedName =
    selectedUser?.name || selectedUser?.loginname || 'Unknown';
  const selectedIsCurrent = selectedUser?.recnum === currentUser?.recnum;

  const handleSelect = (userId: number) => {
    const isCurrentUser = userId === currentUser?.recnum;
    onUserChange(isCurrentUser ? null : userId);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-10 w-[220px] min-w-0 shrink justify-between"
        >
          <span className="flex min-w-0 items-center gap-2">
            {selectedUser && (
              <UserAvatar
                recnum={selectedUser.recnum}
                name={selectedUser.name}
                loginname={selectedUser.loginname}
                size="sm"
              />
            )}
            <span className="truncate">
              {selectedUser ? selectedName : 'Select user...'}
              {selectedIsCurrent && (
                <span className="text-muted-foreground ml-1">(You)</span>
              )}
            </span>
          </span>
          <ChevronsUpDown className="text-muted-foreground ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search user..." />
          <CommandList>
            <CommandEmpty>No user found.</CommandEmpty>
            <CommandGroup>
              {internalUsers.map((user) => {
                const userName = user.name || user.loginname || 'Unknown';
                const isCurrent = user.recnum === currentUser?.recnum;
                const isSelected = user.recnum === effectiveUserId;
                return (
                  <CommandItem
                    key={user.recnum}
                    value={`${userName} ${user.loginname ?? ''}`}
                    onSelect={() => handleSelect(user.recnum)}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        isSelected ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <UserAvatar
                      recnum={user.recnum}
                      name={user.name}
                      loginname={user.loginname}
                      size="sm"
                    />
                    <span className="ml-2 flex-1 truncate">
                      {userName}
                      {isCurrent && (
                        <span className="text-muted-foreground ml-1">
                          (You)
                        </span>
                      )}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
